<template>
    <div class="layout-main-container">
        <div class="layout-main">
            <div style="display: flex;justify-content: center;" v-if="notificationsUpdates > 0"
                @click="loadNotifications">
                <div class="floating">
                    <font-awesome-icon :icon="['fas', 'bell']" size="xl" shake style="color: white;" />
                </div>
            </div>
            <div class=" card notification_container" v-for="n in notifications" :key="n" :id="'n_' + n.id">
                <div class="flex justify-content-between flex-grow-1">
                    <div class="flex gap-3">
                        <div style="margin-left: 10px;" class="notification_container">
                            <img :src="n.issuer.imageUrl" class="user_avatar" />
                        </div>
                        <div class="flex flex-column flex-wrap justify-content-end">
                            <div class="text-blue-500 text-xs">
                                {{ n.timestamp }}
                            </div>
                            <div>
                                {{ makeNoticationText(n) }}
                            </div>
                        </div>
                    </div>
                    <Divider layout="vertical" />
                </div>
                <div>
                    <div class="flex flex-wrap justify-content-center gap-3">
                        <span class="surface-ground border-circle text-gray-700">
                            <i :class="['pi', 'pi-link']" style="cursor: pointer; font-size: 1.25rem; margin: 0.75rem;"
                                @click="openOperation(n)" />
                        </span>
                        <span v-if="n.type === socketMessageTypeEnum.OPERATION_UPDATE"
                            class="surface-ground border-circle text-gray-700">
                            <i :class="['pi', 'pi-eye-slash']"
                                style="cursor: pointer; font-size: 1.25rem; margin: 0.75rem; " @click="markAsRead(n)"
                                v-styleclass="{ selector: '#n_' + n.id, leaveActiveClass: 'my-fadeout', leaveToClass: 'hidden' }" />
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<script setup>
import { onErrorCaptured, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { handleError } from '@/helpers/ErrorHandler'
import { useToast } from "primevue/usetoast"
import { useAuthStore } from '@/stores/auth'
import { useGlobalStore } from '@/stores/global'
import { useI18n } from 'vue-i18n'
import notificationService from '@/service/NotificationService'
import userService from '@/service/UserService'
import utils from '@/helpers/Utils'
import { socketMessageTypeEnum, operationTypeEnum, operationStatusEnum } from '@/helpers/Constants'
import router from '@/router/router'
import remove from 'lodash/remove'
import format from '@stdlib/string-format'
import socketService from '@/service/SocketService'
import SocketListener from '@/model/SocketListener'
import Divider from 'primevue/divider';

const authStore = useAuthStore()
const globalStore = useGlobalStore()
const toast = useToast()
const i18n = useI18n()

const notifications = ref([])
const notificationsUpdates = ref(0)

let uniqueId

onBeforeUnmount(() => {
    socketService.removeListener(uniqueId)
})

onBeforeMount(async () => {
    uniqueId = 'NOTIFICATIONS_' + new Date().getTime()
    socketService.addListener(socketMessageTypeEnum.UNREAD_NOTIFICATIONS_COUNT, new SocketListener(uniqueId, handleInstantNotification))
    loadNotifications()
})

const loadNotifications = async () => {
    notificationsUpdates.value = 0
    try {
        const response = await notificationService.loadUnreadNotifications()
        notifications.value = response.filter(n => n.type === socketMessageTypeEnum.OPERATION_UPDATE || n.type === socketMessageTypeEnum.BOOK_RETURN_REMINDER)
        if (authStore.amIAdmin()) {
            response.filter(n => n.type === socketMessageTypeEnum.OPERATION_INCIDENCE).forEach(i => notifications.value.push(i))
        }
        userService.decorate(notifications.value.map(o => o.issuer))
        notifications.value.forEach(n => n.timestamp = utils.formatDate(n.timestamp))
    } catch (error) {
        handleError(authStore, toast, i18n, error)
    }
}

onErrorCaptured((err, instance, info) => {
    return handleError(authStore, toast, i18n, err, instance, info)
})

const makeNoticationText = (notification) => {
    if (notification.type === socketMessageTypeEnum.OPERATION_INCIDENCE) {
        return format(i18n.t('USER_FLAGGED_OPERATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
    } else if (notification.type === socketMessageTypeEnum.BOOK_RETURN_REMINDER) {
        return i18n.t('BOOK_RETURN_REMINDER')
    } else {
        const messagePrefix = authStore.amIAdmin() ? 'ADMIN_' : ""
        switch (notification.statusId) {
            case operationStatusEnum.CREATED:
                {
                    if (notification.operation.typeId === operationTypeEnum.GIFT) {
                        return format(i18n.t(messagePrefix + 'GIFT_CREATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
                    } else if (notification.operation.typeId === operationTypeEnum.BORROWING) {
                        return format(i18n.t(messagePrefix + 'BORROWING_CREATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
                    } else {
                        return format(i18n.t(messagePrefix + 'EXCHANGE_CREATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
                    }
                }
            case operationStatusEnum.APPROVED_BY_ADMIN:
                return format(i18n.t('ADMIN_APPROVED_OPERATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
            case operationStatusEnum.REJECTED_BY_ADMIN:
                return format(i18n.t('ADMIN_REJECTED_OPERATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
            case operationStatusEnum.REJECTED:
                return format(i18n.t('USER_REJECTED_OPERATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
            case operationStatusEnum.WAITING_DEAL_CONFIRMATION:
                return format(i18n.t('USER_APPROVED_OPERATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
            case operationStatusEnum.CANCELED:
                return format(i18n.t('USER_CANCELED_OPERATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
            case operationStatusEnum.DEAL_CONFIRMED_BY_SENDER:
            case operationStatusEnum.DEAL_CONFIRMED_BY_RECEIVER:
            case operationStatusEnum.IN_PROGRESS:
                return format(i18n.t('USER_CONFIRMED_OPERATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
            case operationStatusEnum.RETRIEVED_BY_SENDER:
            case operationStatusEnum.RETRIEVED_BY_RECEIVER:
            case operationStatusEnum.COMPLETED:
                {
                    if (notification.operation.expiration !== null && notification.operation.expiration !== undefined) {
                        return format(i18n.t('USER_CONFIRMED_RETRIEVAL_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
                    } else {
                        return format(i18n.t('USER_CONFIRMED_OPERATION_NOTIFICATION_TEMPLATE'), notification.issuer.fullName)
                    }
                }
        }
    }

}

const markAsRead = (notification) => {
    authStore.unreadOperationsMessages--
    setTimeout(function () {
        notificationService.markAsRead(notification)
        remove(notifications.value, n => n.id === notification.id)
    }, 1000)
}

const openOperation = (notification) => {
    authStore.unreadOperationsMessages--
    globalStore.sharedData = notification.operation.id
    notificationService.markAsRead(notification)
    authStore.amIAdmin() ? router.push('/controlPanel') : router.push('/operations')
}

const handleInstantNotification = () => {
    notificationsUpdates.value++
}

</script>

<style scoped>
.user_avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
}

.card {
direction: rtl;
  background-color: var(--surface-section);
  
  border: 1px solid var(--surface-border);
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: var(--card-shadow);
  border-radius: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.notification_container {
    direction: rtl;
    display: flex;
    align-items: center;
    justify-content: space-between;

}

</style>